// Technique Library metadata for the Analysis Workbench.
// `category` = the horizontal tab strip. `group` = the sub-heading within
// that tab's Technique Library list. `engineId` (when present) is the actual
// backend analysisType to call — lets a library entry be a differently-framed
// view of the same engine (e.g. "Driver Analysis" reuses the regression engine).
// `status: 'comingSoon'` entries are shown (for information architecture
// completeness, matching the reference design) but are disabled — we don't
// fake a result for techniques with no backend implementation.

const CONFIDENCE_LEVELS = [
  { value: 0.90, label: "90%" },
  { value: 0.95, label: "95%" },
  { value: 0.99, label: "99%" },
];

// "meanImpute" is a real, different computation (see analysis_engine._prepare_missing) —
// not a cosmetic option. Predictor gaps are filled with the column mean before
// fitting; rows missing the dependent variable are still dropped either way.
const MISSING_VALUES_OPTIONS = [
  { value: "listwise", label: "Listwise" },
  { value: "meanImpute", label: "Mean imputation" },
];

const analysisTypes = [
  // ── Descriptives ────────────────────────────────────────────────────────
  {
    id: "descriptive", label: "Descriptives", category: "Descriptives", group: "Descriptive & Exploratory",
    description: "Mean, median, mode, spread and shape for one or more numeric variables.",
    status: "ready",
    params: [
      { name: "variables", label: "Variables", role: "numeric", multiple: true, required: true },
    ],
  },
  {
    id: "frequencies", label: "Frequencies", category: "Descriptives", group: "Descriptive & Exploratory",
    description: "Value counts and percentages for categorical variables.", status: "comingSoon", params: [],
  },
  {
    id: "crosstabs", label: "Crosstabs", category: "Descriptives", group: "Descriptive & Exploratory",
    description: "Cross-tabulation with column comparisons — see the Crosstabs page.", status: "comingSoon", params: [],
  },
  {
    id: "correlation", label: "Correlation", category: "Descriptives", group: "Descriptive & Exploratory",
    description: "Pearson/Spearman correlation with p-values and a heatmap.",
    status: "ready",
    params: [
      { name: "variables", label: "Variables", role: "numeric", multiple: true, required: true, min: 2 },
      {
        name: "method", label: "Method", type: "select", default: "pearson",
        options: [{ value: "pearson", label: "Pearson" }, { value: "spearman", label: "Spearman" }],
      },
    ],
  },
  {
    id: "reliability", label: "Reliability (Cronbach's Alpha)", category: "Descriptives", group: "Descriptive & Exploratory",
    description: "Internal-consistency check for a multi-item scale/battery.",
    status: "ready",
    params: [
      { name: "variables", label: "Scale items", role: "numeric", multiple: true, required: true, min: 2 },
    ],
  },

  // ── Comparative ─────────────────────────────────────────────────────────
  {
    id: "ttest", label: "T-Test", category: "Comparative", group: "Comparative",
    description: "Compare the mean of a numeric variable across 2 groups.",
    status: "ready",
    metricTiles: [
      { key: "t_stat", label: "t", decimals: 2 },
      { key: "p_value", label: "p", decimals: 4 },
      { key: "cohens_d", label: "Cohen's d", decimals: 2 },
    ],
    params: [
      { name: "dv", label: "Test variable", role: "numeric", multiple: false, required: true, zone: "Dependent Variable" },
      { name: "groupVar", label: "Grouping variable (2 groups)", role: "categorical", multiple: false, required: true, zone: "Grouping Variable" },
      { name: "confidenceLevel", label: "Confidence level", type: "select", default: 0.95, options: CONFIDENCE_LEVELS },
    ],
  },
  {
    id: "anova", label: "ANOVA / MANOVA", category: "Comparative", group: "Comparative",
    description: "Compare the mean of a numeric variable across 3+ groups, with Tukey post-hoc.",
    status: "ready",
    params: [
      { name: "dv", label: "Test variable", role: "numeric", multiple: false, required: true, zone: "Dependent Variable" },
      { name: "groupVar", label: "Grouping variable (3+ groups)", role: "categorical", multiple: false, required: true, zone: "Grouping Variable" },
    ],
  },
  {
    id: "chisquare", label: "Chi-Square Test", category: "Comparative", group: "Comparative",
    description: "Test whether two categorical variables are associated.",
    status: "ready",
    params: [
      { name: "var1", label: "Variable 1", role: "categorical", multiple: false, required: true, zone: "Variable 1" },
      { name: "var2", label: "Variable 2", role: "categorical", multiple: false, required: true, zone: "Variable 2" },
    ],
  },

  // ── Predictive Modeling (tab: Regression) ──────────────────────────────
  {
    id: "regression", label: "Linear Regression", category: "Regression", group: "Predictive Modeling",
    description: "Model the effect of numeric predictors on a numeric outcome.",
    status: "ready",
    metricTiles: [
      { key: "r", label: "R", decimals: 3 },
      { key: "r2", label: "R²", decimals: 3 },
      { key: "adjusted_r2", label: "Adj. R²", decimals: 3 },
      { key: "std_error", label: "Std. Error", decimals: 2 },
    ],
    params: [
      { name: "dv", label: "Dependent Variable", role: "numeric", multiple: false, required: true, zone: "Dependent Variable" },
      { name: "ivs", label: "Independent Variables (Predictors)", role: "numeric", multiple: true, required: true, zone: "Independent Variables" },
      {
        name: "method", label: "Method", type: "select", default: "enter",
        options: [{ value: "enter", label: "Enter" }, { value: "stepwise", label: "Stepwise" }],
      },
      { name: "missingValues", label: "Missing", type: "select", default: "listwise", options: MISSING_VALUES_OPTIONS },
      { name: "confidenceLevel", label: "CI", type: "select", default: 0.95, options: CONFIDENCE_LEVELS },
      {
        name: "statsAndPlots", label: "Statistics & Plots", type: "checklist",
        items: [
          { name: "showModelFit", label: "Model fit", default: true },
          { name: "showCoefficients", label: "Coefficients", default: true },
          { name: "showCI", label: "Confidence intervals", default: true },
          { name: "showVif", label: "Collinearity diagnostics (VIF)", default: false },
          { name: "showResidualPlot", label: "Residual plots", default: false },
        ],
      },
    ],
  },
  {
    id: "driverAnalysis", label: "Driver Analysis", category: "Segmentation", group: "Segmentation & Reduction",
    engineId: "regression",
    description: "Rank the predictors that most drive a numeric outcome (standardized-beta view of regression).",
    status: "ready",
    params: [
      { name: "dv", label: "Outcome", role: "numeric", multiple: false, required: true, zone: "Dependent Variable" },
      { name: "ivs", label: "Candidate drivers", role: "numeric", multiple: true, required: true, zone: "Independent Variables" },
      { name: "confidenceLevel", label: "Confidence level", type: "select", default: 0.95, options: CONFIDENCE_LEVELS },
    ],
  },
  {
    id: "randomforest", label: "Random Forest Regression", category: "Regression", group: "Predictive Modeling",
    description: "Non-linear alternative to Linear Regression; ranks predictors by IncNodePurity.",
    status: "ready",
    metricTiles: [
      { key: "oob_r2", label: "OOB R²", decimals: 3 },
      { key: "oob_rmse", label: "OOB RMSE", decimals: 2 },
      { key: "oob_mae", label: "OOB MAE", decimals: 2 },
      { key: "n", label: "N", decimals: 0 },
    ],
    params: [
      { name: "dv", label: "Dependent Variable", role: "numeric", multiple: false, required: true, zone: "Dependent Variable" },
      { name: "ivs", label: "Independent Variables (Predictors)", role: "numeric", multiple: true, required: true, zone: "Independent Variables" },
      { name: "missingValues", label: "Missing", type: "select", default: "listwise", options: MISSING_VALUES_OPTIONS },
      { name: "nEstimators", label: "Number of trees", type: "number", default: 200 },
      { name: "maxDepth", label: "Max tree depth (blank = unlimited)", type: "number", optional: true },
      {
        name: "statsAndPlots", label: "Statistics & Plots", type: "checklist",
        items: [
          { name: "showModelFit", label: "Model fit", default: true },
          { name: "showCoefficients", label: "Variable importance", default: true },
          { name: "showPredActualPlot", label: "Predicted vs. actual plot", default: true },
        ],
      },
    ],
  },
  {
    id: "logistic", label: "Logistic Regression", category: "Regression", group: "Predictive Modeling",
    description: "Predict a binary outcome; reports odds ratios, accuracy, ROC/AUC.",
    status: "ready",
    metricTiles: [
      { key: "accuracy", label: "Accuracy", percent: true },
      { key: "auc", label: "AUC", decimals: 3 },
      { key: "n", label: "N", decimals: 0 },
    ],
    params: [
      { name: "dv", label: "Outcome (binary)", role: "categorical", multiple: false, required: true, zone: "Dependent Variable" },
      { name: "ivs", label: "Independent Variables (Predictors)", role: "numeric", multiple: true, required: true, zone: "Independent Variables" },
      { name: "missingValues", label: "Missing", type: "select", default: "listwise", options: MISSING_VALUES_OPTIONS },
      { name: "confidenceLevel", label: "CI", type: "select", default: 0.95, options: CONFIDENCE_LEVELS },
      {
        name: "statsAndPlots", label: "Statistics & Plots", type: "checklist",
        items: [
          { name: "showModelFit", label: "Model fit", default: true },
          { name: "showCoefficients", label: "Coefficients", default: true },
          { name: "showCI", label: "Odds-ratio confidence intervals", default: true },
          { name: "showRoc", label: "ROC curve", default: false },
        ],
      },
    ],
  },
  {
    id: "discriminant", label: "Discriminant Analysis", category: "Regression", group: "Predictive Modeling",
    description: "Classify cases into known groups using multiple predictors.", status: "comingSoon", params: [],
  },

  // ── Segmentation & Reduction ────────────────────────────────────────────
  {
    id: "cluster", label: "Cluster / Segmentation", category: "Segmentation", group: "Segmentation & Reduction",
    description: "Group respondents into segments based on numeric variables.",
    status: "ready",
    params: [
      { name: "variables", label: "Segmentation variables", role: "numeric", multiple: true, required: true, min: 2 },
      { name: "k", label: "Number of segments (blank = auto)", type: "number", optional: true },
    ],
  },
  {
    id: "pca", label: "Factor Analysis (PCA)", category: "Segmentation", group: "Segmentation & Reduction",
    description: "Reduce many numeric variables down to their main underlying dimensions.",
    status: "ready",
    params: [
      { name: "variables", label: "Variables", role: "numeric", multiple: true, required: true, min: 3 },
      { name: "nComponents", label: "Components to show", type: "number", default: 2 },
    ],
  },

  // ── Choice Modeling ─────────────────────────────────────────────────────
  {
    id: "maxdiff", label: "MaxDiff", category: "Choice Modeling", group: "Choice Modeling",
    description: "Best-worst scaling utility scores.", status: "comingSoon", params: [],
  },
  {
    id: "conjoint", label: "Conjoint", category: "Choice Modeling", group: "Choice Modeling",
    description: "Part-worth utilities from a conjoint design.", status: "comingSoon", params: [],
  },
  {
    id: "turf", label: "TURF (Reach & Frequency)", category: "Choice Modeling", group: "Choice Modeling",
    description: "Find the combination of items that reaches the most respondents.",
    status: "ready",
    params: [
      { name: "variables", label: "Reach items (binary)", role: "categorical", multiple: true, required: true, min: 2 },
      { name: "nItems", label: "Combination size", type: "number", default: 3 },
    ],
  },

  // ── Data Preparation ────────────────────────────────────────────────────
  // These three don't fit the technique-run model (dv/ivs -> table/chart output).
  // Instead they add a step to a cross-cutting "recipe" that stays active while
  // you switch between real techniques, applied to the data before each run.
  {
    id: "prepFilter", label: "Filter / Exclusions", category: "Data Prep", group: "Data Preparation",
    description: "Exclude cases that don't match a logic expression before analysis.",
    status: "ready", isPrepStep: true, prepKind: "filter", params: [],
  },
  {
    id: "prepRecode", label: "Recode Values", category: "Data Prep", group: "Data Preparation",
    description: "Reverse-code a scale, remap values, or set specific codes to missing.",
    status: "ready", isPrepStep: true, prepKind: "recode", params: [],
  },
  {
    id: "prepStack", label: "Stack Grid Variables", category: "Data Prep", group: "Data Preparation",
    description: "Melt a grid's columns into long-format rows for analysis.",
    status: "ready", isPrepStep: true, prepKind: "stack", params: [],
  },
  {
    id: "weighting", label: "Weighting", category: "Data Prep", group: "Data Preparation",
    description: "RIM/target weighting — available on the Weighting page.", status: "comingSoon", params: [],
  },
  {
    id: "imputation", label: "Missing Data Imputation", category: "Data Prep", group: "Data Preparation",
    description: "Fill missing values via mean/regression/multiple imputation.", status: "comingSoon", params: [],
  },
];

export const CATEGORIES = ["Descriptives", "Comparative", "Regression", "Segmentation", "Choice Modeling", "Data Prep"];

export default analysisTypes;
